import { Customer, Subscription, Invoice, PlanDetail, SubscriptionStatus, PlanTier } from '../types';
import { api } from './api';

export interface CheckoutSuccessPayload {
  plan: PlanDetail;
  billingCycle: 'monthly' | 'quarterly' | 'annually';
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  country?: string;
  gstin?: string;
  workspaces?: number;
  finalAmount: number;
  taxableAmount: number;
  gstAmount: number;
  gateway: string;
  paymentId?: string;
  appliedCouponCode?: string;
}

export async function processPostCheckoutSuccess(payload: CheckoutSuccessPayload): Promise<{
  customer: Customer;
  subscription: Subscription;
  invoice: Invoice;
}> {
  const {
    plan,
    billingCycle,
    customerName,
    customerEmail,
    customerPhone,
    country = 'IN',
    gstin = '',
    workspaces = 1,
    finalAmount,
    taxableAmount,
    gstAmount,
    gateway,
    paymentId,
  } = payload;

  const now = new Date();
  const nowIso = now.toISOString();

  // 1. Calculate next billing date
  const nextBillingDate = new Date(now);
  if (billingCycle === 'annually') {
    nextBillingDate.setFullYear(nextBillingDate.getFullYear() + 1);
  } else if (billingCycle === 'quarterly') {
    nextBillingDate.setMonth(nextBillingDate.getMonth() + 3);
  } else {
    nextBillingDate.setMonth(nextBillingDate.getMonth() + 1);
  }

  // 2. Calculate Monthly Normalized MRR
  let mrr = finalAmount;
  if (billingCycle === 'annually') {
    mrr = Math.round(finalAmount / 12);
  } else if (billingCycle === 'quarterly') {
    mrr = Math.round(finalAmount / 3);
  }

  // 3. Resolve Customer Record
  const searchParams = new URLSearchParams(window.location.search);
  const explicitCustomerId = searchParams.get('customer_id') || searchParams.get('customerId') || '';
  const isPortalUpgrade = localStorage.getItem('is_customer_session') === 'true' && Boolean(localStorage.getItem('portal_customer_id'));
  let resolvedCustomerId = explicitCustomerId || (isPortalUpgrade ? localStorage.getItem('portal_customer_id') || '' : '');

  let existingCustomer: Customer | null = null;
  try {
    const allCustomers = await api.customers.getAll();
    existingCustomer = allCustomers.find(c => {
      const idMatches = Boolean(resolvedCustomerId) && c.id === resolvedCustomerId;
      const emailMatches = Boolean(customerEmail) && c.email?.toLowerCase() === customerEmail.toLowerCase();
      return idMatches || emailMatches;
    }) || null;
  } catch (err) {
    console.warn('Could not query existing customers', err);
  }

  if (!existingCustomer && (!resolvedCustomerId || resolvedCustomerId.startsWith('cust_temp'))) {
    resolvedCustomerId = `cust_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
  }

  const customerId = existingCustomer ? existingCustomer.id : resolvedCustomerId;
  const totalLtv = (existingCustomer?.totalLtv || 0) + finalAmount;
  const initialCredits = plan.aiCredits || 5000;

  const customerData: Customer = {
    id: customerId,
    name: customerName || existingCustomer?.name || (customerEmail ? customerEmail.split('@')[0] : 'Valued Customer'),
    email: customerEmail || existingCustomer?.email || 'customer@example.com',
    phone: customerPhone || existingCustomer?.phone || '',
    location: country === 'IN' ? 'India' : country === 'US' ? 'United States' : country,
    gstin: gstin || existingCustomer?.gstin || (country === 'IN' ? `27AABCU${Math.floor(1000 + Math.random() * 9000)}R1ZM` : undefined),
    riskLevel: 'Low',
    signupDate: existingCustomer?.signupDate || nowIso.split('T')[0],
    lastActive: 'Just now',
    totalLtv,
    credits: {
      balance: (existingCustomer?.credits?.balance || 0) + initialCredits,
      used: existingCustomer?.credits?.used || 0,
      totalAllocated: (existingCustomer?.credits?.totalAllocated || 0) + initialCredits,
    },
  };

  if (existingCustomer) {
    await api.customers.update(customerId, customerData);
  } else {
    await api.customers.create(customerData);
  }

  // 4. Create Active Subscription Record
  const subscriptionId = `sub_${Date.now()}`;
  const subscriptionData: Subscription = {
    id: subscriptionId,
    gatewayRef: paymentId || `pay_${Date.now()}`,
    customerId,
    customerName: customerData.name,
    plan: (plan.tier as PlanTier) || PlanTier.STARTER,
    status: SubscriptionStatus.ACTIVE,
    mrr,
    billingCycle: billingCycle === 'annually' ? 'Annually' : 'Monthly',
    startDate: nowIso,
    nextBillingDate: nextBillingDate.toISOString(),
    autoRenew: true,
  };

  await api.subscriptions.create(subscriptionData);

  // 5. Generate Paid Invoice Record
  const invoiceNumber = `INV-${now.getFullYear()}-${Math.floor(100000 + Math.random() * 900000)}`;
  const invoiceId = `inv_${Date.now()}`;
  const invoiceData: Invoice = {
    id: invoiceId,
    invoiceNumber,
    customerId,
    customerName: customerData.name,
    planTier: (plan.tier as PlanTier) || PlanTier.STARTER,
    amount: finalAmount,
    currency: plan.currency || '$',
    status: 'Paid',
    date: nowIso.split('T')[0],
    pdfUrl: `/api/v1/invoices/${invoiceId}/pdf`,
  };

  await api.invoices.create(invoiceData);

  // 6. Update local session storage so Customer Portal & Admin immediately reflect data
  localStorage.setItem('portal_customer_id', customerId);
  localStorage.setItem('subscale_customer_id', customerId);
  localStorage.setItem('is_customer_session', 'true');

  return {
    customer: customerData,
    subscription: subscriptionData,
    invoice: invoiceData,
  };
}
